/*
 * Copyright (C) 2026 Motorola Solutions, Inc.
 * All Rights Reserved.
 * Motorola Solutions Confidential Restricted.
 */

import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import Icon from '@msi/cobalt-react/icon';
import {
  PageEvent,
  PrimeTableColumn,
  PrimeTableProps,
  PrimeTableRef,
} from './PrimeTable.types';
import {
  calculatePageCount,
  exportCSV,
  getNestedValue,
  isSelected,
  paginateData,
  sortData,
  toggleSelection,
} from './PrimeTable.utils';
import PrimeTableHeader from './PrimeTableHeader';
import PrimeTableBody from './PrimeTableBody';
import PrimeTablePaginator from './PrimeTablePaginator';
import './PrimeTable.css';

export const PrimeTable = forwardRef<PrimeTableRef, PrimeTableProps>((props, ref) => {
  const {
    value = [],
    columns = [],
    dataKey,
    paginator = false,
    first: controlledFirst,
    rows = 10,
    rowsPerPageOptions = [10, 20, 50],
    totalRecords,
    lazy = false,
    sortField: initialSortField,
    sortOrder: initialSortOrder,
    selectionMode,
    selection: controlledSelection,
    compareSelectionBy = 'equals',
    metaKeySelection = true,
    scrollable = false,
    scrollHeight = '400px',
    expandedRows: controlledExpandedRows,
    rowExpansionTemplate,
    frozenColumns: controlledFrozenColumns,
    emptyMessage = 'No records found',
    loading = false,
    size = 'normal',
    stripedRows = false,
    showGridlines = false,
    className = '',
    style,
    exportFilename = 'export',
    headerTemplate,
    footerTemplate,
    captionTemplate,
    onSort,
    onPage,
    onRowClick,
    onRowSelect,
    onRowUnselect,
    onSelectionChange,
    onRowExpand,
    onRowCollapse,
    onColumnPin,
  } = props;

  // State
  const [internalSelection, setInternalSelection] = useState<any>(null);
  const [sortField, setSortField] = useState<string | undefined>(initialSortField);
  const [sortOrder, setSortOrder] = useState<1 | -1 | undefined>(initialSortOrder);
  const [first, setFirst] = useState(controlledFirst !== undefined ? controlledFirst : 0);
  const [currentRows, setCurrentRows] = useState(rows);
  const [expandedRows, setExpandedRows] = useState<any[]>(controlledExpandedRows || []);
  const [internalFrozenColumns, setInternalFrozenColumns] = useState<Set<string>>(new Set(
    controlledFrozenColumns || columns.filter(col => col.frozen).map(col => col.field as string)
  ));
  const [columnWidths, setColumnWidths] = useState<{ [key: string]: string }>({});
  const [resizingColumn, setResizingColumn] = useState<string | null>(null);
  const [resizeStartX, setResizeStartX] = useState<number>(0);
  const [resizeStartWidth, setResizeStartWidth] = useState<number>(0);

  // Ref for container to measure available width
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Use controlled or internal selection
  const selection = controlledSelection !== undefined ? controlledSelection : internalSelection;
  
  // Use controlled or internal frozen columns - memoize the Set creation
  // When controlled: create Set from array. When internal: use the Set directly.
  const isControlled = controlledFrozenColumns !== undefined;
  const frozenColumns = useMemo(() => {
    if (isControlled) {
      return new Set(controlledFrozenColumns!);
    }
    return internalFrozenColumns;
  }, [isControlled, controlledFrozenColumns?.join(','), internalFrozenColumns]);

  // Sync internal first state with controlled first prop
  useEffect(() => {
    if (controlledFirst !== undefined) {
      setFirst(controlledFirst);
    }
  }, [controlledFirst]);

  // Sync internal frozen columns state with controlled frozen columns prop
  // This ensures internal state matches controlled prop for consistent behavior
  useEffect(() => {
    if (controlledFrozenColumns !== undefined) {
      setInternalFrozenColumns(new Set(controlledFrozenColumns));
    }
  }, [controlledFrozenColumns?.join(',')]);

  // Initialize column widths to fill available space
  useEffect(() => {
    // Only run if we haven't set widths yet and have a container
    if (Object.keys(columnWidths).length > 0 || !containerRef.current) return;

    const visibleColumns = columns.filter(col => col.visible !== false);
    if (visibleColumns.length === 0) return;

    // Get available width from container (account for potential scrollbar)
    const containerWidth = containerRef.current.offsetWidth;
    
    // Calculate fixed width for expansion and selection columns
    let fixedWidth = 0;
    if (rowExpansionTemplate !== undefined) fixedWidth += 48;
    if (selectionMode === 'checkbox') fixedWidth += 48;

    // Calculate minimum widths for all columns
    const minWidths: { [key: string]: number } = {};
    let totalMinWidth = 0;
    
    visibleColumns.forEach(col => {
      const fieldKey = col.field as string;
      // Parse width or use default 150px, but enforce absolute minimum of 150px for icons + text
      const configuredWidth = col.width ? parseInt(col.width as string) || 150 : 150;
      const minWidth = Math.max(configuredWidth, 110);
      minWidths[fieldKey] = minWidth;
      totalMinWidth += minWidth;
    });

    // Available width for data columns (subtract a small buffer for borders/padding)
    const buffer = 12;
    const availableWidth = containerWidth - fixedWidth - buffer; 
    
    // Calculate widths
    const newWidths: { [key: string]: string } = {};
    
    if (availableWidth > totalMinWidth) {
      // There's extra space - distribute proportionally
      const extraSpace = availableWidth - totalMinWidth;
      const extraPerColumn = Math.floor(extraSpace / visibleColumns.length);
      const remainder = extraSpace - (extraPerColumn * visibleColumns.length);
      
      let distributedWidth = 0;
      visibleColumns.forEach((col, index) => {
        const fieldKey = col.field as string;
        let finalWidth = minWidths[fieldKey] + extraPerColumn;
        
        // Give remainder pixels to the last column to ensure perfect fit
        if (index === visibleColumns.length - 1) {
          finalWidth += remainder;
        }
        
        newWidths[fieldKey] = `${finalWidth}px`;
        distributedWidth += finalWidth;
      });
    } else {
      // No extra space - use minimum widths
      visibleColumns.forEach(col => {
        const fieldKey = col.field as string;
        newWidths[fieldKey] = `${minWidths[fieldKey]}px`;
      });
    }

    setColumnWidths(newWidths);
  }, [columns, rowExpansionTemplate, selectionMode]);

  // Reset pagination to first page when data length changes (e.g., after filtering or searching)
  const dataLength = value.length;
  useEffect(() => {
    if (controlledFirst === undefined) {
      setFirst(0);
    }
  }, [dataLength, controlledFirst]);

  // Process data
  const processedData = useMemo(() => {
    if (lazy) {
      return value;
    }

    let data = [...value];

    // Apply sorting
    data = sortData(data, sortField, sortOrder);

    return data;
  }, [value, lazy, sortField, sortOrder]);

  // Paginated data
  const paginatedData = useMemo(() => {
    if (!paginator || lazy) {
      return processedData;
    }
    return paginateData(processedData, first, currentRows);
  }, [processedData, paginator, lazy, first, currentRows]);

  // Total records for pagination
  const recordCount = totalRecords !== undefined ? totalRecords : processedData.length;
  const pageCount = calculatePageCount(recordCount, currentRows);
  const currentPage = Math.floor(first / currentRows);

  // Handle sorting
  const handleSort = useCallback(
    (field: string) => {
      let newOrder: 1 | -1 | undefined;

      if (sortField === field) {
        newOrder = sortOrder === 1 ? -1 : sortOrder === -1 ? undefined : 1;
      } else {
        newOrder = 1;
      }

      setSortField(newOrder ? field : undefined);
      setSortOrder(newOrder);

      if (onSort) {
        onSort({
          sortField: newOrder ? field : undefined,
          sortOrder: newOrder,
        });
      }
    },
    [sortField, sortOrder, onSort]
  );

  // Handle pagination
  const handlePageChange = useCallback(
    (newFirst: number, newRows?: number) => {
      const updatedRows = newRows || currentRows;
      setFirst(newFirst);
      if (newRows) {
        setCurrentRows(newRows);
      }

      if (onPage) {
        const event: PageEvent = {
          first: newFirst,
          rows: updatedRows,
          page: Math.floor(newFirst / updatedRows),
          pageCount: calculatePageCount(recordCount, updatedRows),
        };
        onPage(event);
      }
    },
    [currentRows, recordCount, onPage]
  );

  // Handle row selection
  const handleRowClick = useCallback(
    (item: any, event: React.MouseEvent | React.ChangeEvent, rowIndex: number) => {
      if (!selectionMode) return;

      // For checkbox mode, handle multiple selection by toggling
      if (selectionMode === 'checkbox') {
        const currentSelection = Array.isArray(selection) ? selection : [];
        const isCurrentlySelected = isSelected(item, selection, dataKey, compareSelectionBy);
        
        const newSelection = isCurrentlySelected
          ? currentSelection.filter(
              (selectedItem) =>
                !isSelected(selectedItem, [item], dataKey, compareSelectionBy)
            )
          : [...currentSelection, item];

        if (controlledSelection === undefined) {
          setInternalSelection(newSelection);
        }

        if (onSelectionChange) {
          onSelectionChange(newSelection);
        }

        if (!isCurrentlySelected && onRowSelect) {
          onRowSelect({ originalEvent: event as React.MouseEvent, data: item, index: rowIndex });
        } else if (isCurrentlySelected && onRowUnselect) {
          onRowUnselect({ originalEvent: event as React.MouseEvent, data: item, index: rowIndex });
        }

        return;
      }

      // Original logic for single/multiple mode
      const metaKey = (event as React.MouseEvent).ctrlKey || (event as React.MouseEvent).metaKey;
      const newSelection = toggleSelection(
        item,
        selection,
        selectionMode,
        dataKey,
        metaKeySelection,
        metaKey
      );

      const wasSelected = isSelected(item, selection, dataKey, compareSelectionBy);

      if (controlledSelection === undefined) {
        setInternalSelection(newSelection);
      }

      if (onSelectionChange) {
        onSelectionChange(newSelection);
      }

      if (!wasSelected && onRowSelect) {
        onRowSelect({ originalEvent: event as React.MouseEvent, data: item, index: rowIndex });
      } else if (wasSelected && onRowUnselect) {
        onRowUnselect({ originalEvent: event as React.MouseEvent, data: item, index: rowIndex });
      }
    },
    [
      selectionMode,
      selection,
      dataKey,
      metaKeySelection,
      compareSelectionBy,
      controlledSelection,
      onSelectionChange,
      onRowSelect,
      onRowUnselect,
    ]
  );

  // Handle select all checkbox
  const handleSelectAll = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!selectionMode || selectionMode === 'single') return;

      const currentSelection = Array.isArray(selection) ? selection : [];
      const dataToSelect = processedData;

      if (event.target.checked) {
        // Select all rows in current page (or all filtered data)
        const newSelection = [...currentSelection];
        
        dataToSelect.forEach((item: any) => {
          const alreadySelected = isSelected(item, currentSelection, dataKey, compareSelectionBy);
          if (!alreadySelected) {
            newSelection.push(item);
          }
        });
        
        if (controlledSelection === undefined) {
          setInternalSelection(newSelection);
        }

        if (onSelectionChange) {
          onSelectionChange(newSelection);
        }
      } else {
        // Deselect all rows in current page (or all filtered data)
        const newSelection = currentSelection.filter(
          (selectedItem) => {
            return !dataToSelect.some((item: any) => 
              isSelected(selectedItem, [item], dataKey, compareSelectionBy)
            );
          }
        );
        
        if (controlledSelection === undefined) {
          setInternalSelection(newSelection);
        }

        if (onSelectionChange) {
          onSelectionChange(newSelection);
        }
      }
    },
    [
      selectionMode,
      selection,
      processedData,
      dataKey,
      compareSelectionBy,
      controlledSelection,
      onSelectionChange,
    ]
  );

  // Check if all visible rows are selected
  const isAllSelected = useCallback(() => {
    if (!selectionMode || selectionMode === 'single') return false;
    const dataToCheck = processedData;
    if (dataToCheck.length === 0) return false;
    
    const currentSelection = Array.isArray(selection) ? selection : [];
    return dataToCheck.every((item: any) => 
      isSelected(item, currentSelection, dataKey, compareSelectionBy)
    );
  }, [selectionMode, processedData, selection, dataKey, compareSelectionBy]);

  // Check if some (but not all) visible rows are selected
  const isSomeSelected = useCallback(() => {
    if (!selectionMode || selectionMode === 'single') return false;
    const dataToCheck = processedData;
    if (dataToCheck.length === 0) return false;
    
    const currentSelection = Array.isArray(selection) ? selection : [];
    const selectedCount = dataToCheck.filter((item: any) => 
      isSelected(item, currentSelection, dataKey, compareSelectionBy)
    ).length;
    
    return selectedCount > 0 && selectedCount < dataToCheck.length;
  }, [selectionMode, processedData, selection, dataKey, compareSelectionBy]);

  // Handle row expansion
  const handleRowExpand = useCallback(
    (item: any, rowIndex: number) => {
      const itemKey = dataKey ? getNestedValue(item, dataKey) : item;
      const isExpanded = expandedRows.some((row) => {
        const rowKey = dataKey ? getNestedValue(row, dataKey) : row;
        return rowKey === itemKey;
      });

      let newExpandedRows: any[];
      if (isExpanded) {
        newExpandedRows = expandedRows.filter((row) => {
          const rowKey = dataKey ? getNestedValue(row, dataKey) : row;
          return rowKey !== itemKey;
        });
        if (onRowCollapse) {
          onRowCollapse({ data: item, index: rowIndex });
        }
      } else {
        newExpandedRows = [...expandedRows, item];
        if (onRowExpand) {
          onRowExpand({ data: item, index: rowIndex });
        }
      }

      setExpandedRows(newExpandedRows);
    },
    [expandedRows, dataKey, onRowExpand, onRowCollapse]
  );

  // Handle column pin/unpin
  const handleColumnPin = useCallback(
    (field: string) => {
      const newFrozen = new Set(frozenColumns);
      if (newFrozen.has(field)) {
        newFrozen.delete(field);
      } else {
        newFrozen.add(field);
      }
      
      const frozenArray = Array.from(newFrozen);
      
      // If using controlled frozen columns, notify parent
      if (controlledFrozenColumns !== undefined && onColumnPin) {
        onColumnPin(frozenArray);
      } else {
        // Otherwise update internal state
        setInternalFrozenColumns(newFrozen);
      }
    },
    [frozenColumns, controlledFrozenColumns, onColumnPin]
  );

  // Handle column resize start
  const handleResizeStart = useCallback(
    (e: React.MouseEvent, field: string, currentWidth: string) => {
      e.preventDefault();
      e.stopPropagation();
      setResizingColumn(field);
      setResizeStartX(e.clientX);
      // Parse width (remove 'px' if present)
      const widthValue = parseInt(currentWidth) || 150;
      setResizeStartWidth(widthValue);
      // Add class to body to prevent text selection during resize
      document.body.classList.add('resizing-column');
    },
    []
  );

  // Handle column resize
  const handleResize = useCallback(
    (e: MouseEvent) => {
      if (!resizingColumn) return;
      
      const diff = e.clientX - resizeStartX;
      const newWidth = Math.max(110, resizeStartWidth + diff); // Minimum width of 110px to fit sort + pin icons + text
      
      setColumnWidths((prev) => ({
        ...prev,
        [resizingColumn]: `${newWidth}px`,
      }));
    },
    [resizingColumn, resizeStartX, resizeStartWidth]
  );

  // Handle column resize end
  const handleResizeEnd = useCallback(() => {
    setResizingColumn(null);
    // Remove class from body
    document.body.classList.remove('resizing-column');
  }, []);

  // Add/remove global mouse event listeners for resizing
  useEffect(() => {
    if (resizingColumn) {
      window.addEventListener('mousemove', handleResize);
      window.addEventListener('mouseup', handleResizeEnd);
      return () => {
        window.removeEventListener('mousemove', handleResize);
        window.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [resizingColumn, handleResize, handleResizeEnd]);

  // Cleanup body class on unmount
  useEffect(() => {
    return () => {
      document.body.classList.remove('resizing-column');
    };
  }, []);

  // Get column width (use custom width if resized, otherwise default)
  const getColumnWidth = useCallback(
    (field: string, defaultWidth?: string, configMinWidth?: string) => {
      const width = columnWidths[field] || defaultWidth || '150px';
      const widthValue = parseInt(width) || 150;
      const minWidthValue = configMinWidth ? parseInt(configMinWidth) || 110 : 110;
      const finalWidth = Math.max(widthValue, minWidthValue);
      return `${finalWidth}px`;
    },
    [columnWidths]
  );

  // Calculate total table width based on all columns
  const calculateTableWidth = useCallback(() => {
    let totalWidth = 0;

    // Add expansion column width
    if (rowExpansionTemplate !== undefined) {
      totalWidth += 48; // 3rem = 48px
    }

    // Add selection column width
    if (selectionMode === 'checkbox') {
      totalWidth += 48; // 3rem = 48px
    }

    // Add all visible column widths
    columns
      .filter((col) => col.visible !== false)
      .forEach((column) => {
        const fieldKey = column.field as string;
        const currentWidth = getColumnWidth(fieldKey, column.width, column.minWidth);
        const width = parseInt(currentWidth) || 150;
        totalWidth += width;
      });

    return totalWidth;
  }, [columns, rowExpansionTemplate, selectionMode, getColumnWidth]);

  // Check if row is expanded
  const isRowExpanded = useCallback(
    (item: any): boolean => {
      const itemKey = dataKey ? getNestedValue(item, dataKey) : item;
      return expandedRows.some((row) => {
        const rowKey = dataKey ? getNestedValue(row, dataKey) : row;
        return rowKey === itemKey;
      });
    },
    [expandedRows, dataKey]
  );

  // Export to CSV
  const handleExportCSV = useCallback(
    (options?: { selectionOnly?: boolean }) => {
      const dataToExport = options?.selectionOnly
        ? Array.isArray(selection)
          ? selection
          : selection
          ? [selection]
          : []
        : processedData;

      exportCSV(dataToExport, columns, `${exportFilename}.csv`);
    },
    [selection, processedData, columns, exportFilename]
  );

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    exportCSV: handleExportCSV,
    reset: () => {
      setSortField(undefined);
      setSortOrder(undefined);
      setFirst(0);
      setInternalSelection(null);
    },
    getSelection: () => selection
  }));

  // Render sort icon
  const renderSortIcon = (field: string) => {
    if (sortField !== field) {
      return (
        <span className="sort-icon">
          <Icon name="ic_arrow_down" size={12} color="grey" />
        </span>
      );
    }
    return (
      <span className="sort-icon active">
        {sortOrder === 1 ? (
          <Icon name="ic_arrow_up" size={12} color="dark-grey" />
        ) : (
          <Icon name="ic_arrow_down" size={12} color="dark-grey" />
        )}
      </span>
    );
  };

  // Get sorted columns with their original indices for position mapping
  const getSortedColumnsWithIndices = useCallback(() => {
    const visibleCols = columns.filter((col) => col.visible !== false);
    const pinnedItems: { col: PrimeTableColumn; originalIndex: number }[] = [];
    const unpinnedItems: { col: PrimeTableColumn; originalIndex: number }[] = [];

    visibleCols.forEach((col, idx) => {
      const fieldKey = col.field as string;
      const item = { col, originalIndex: idx };
      if (frozenColumns.has(fieldKey)) {
        pinnedItems.push(item);
      } else {
        unpinnedItems.push(item);
      }
    });

    return [...pinnedItems, ...unpinnedItems];
  }, [columns, frozenColumns]);

  // Calculate frozen column positions (reactive to columnWidths changes)
  const frozenPositions = useMemo(() => {
    const positions: { [key: string]: number } = {}; // Changed to use fieldKey instead of index
    let cumulativeLeft = 0;

    // Add expansion column width
    if (rowExpansionTemplate !== undefined) {
      cumulativeLeft += 48; // 3rem = 48px
    }

    // Add selection column width
    if (selectionMode === 'checkbox') {
      cumulativeLeft += 48; // 3rem = 48px
    }

    // Get sorted columns to calculate positions in display order
    const sortedCols = getSortedColumnsWithIndices();

    // Calculate positions for frozen columns in SORTED order (as they appear)
    sortedCols.forEach(({ col: column }) => {
      const fieldKey = column.field as string;
      if (frozenColumns.has(fieldKey)) {
        positions[fieldKey] = cumulativeLeft;
        // Use dynamic width if resized, otherwise use column width or default
        const currentWidth = getColumnWidth(fieldKey, column.width, column.minWidth);
        const width = parseInt(currentWidth) || 150;
        cumulativeLeft += width;
      }
    });

    return positions;
  }, [columns, frozenColumns, rowExpansionTemplate, selectionMode, getColumnWidth, getSortedColumnsWithIndices]);

  // Calculate table classes
  const tableClasses = [
    'prime-table',
    size ? `size-${size}` : '',
    stripedRows ? 'striped' : '',
    showGridlines ? 'show-gridlines' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const wrapperClasses = [
    'prime-table-wrapper',
    scrollable ? 'prime-table-scrollable' : '',
    scrollable ? 'scroll-vertical' : '',
  ]
    .filter(Boolean)
    .join(' ');

  // Calculate table width for proper horizontal scrolling
  const tableWidth = calculateTableWidth();

  return (
    <div className="prime-table-container" ref={containerRef} style={style}>
      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
        </div>
      )}
      {captionTemplate && <div className="prime-table-caption">{captionTemplate()}</div>}
      <div className={wrapperClasses} style={{ '--scroll-height': scrollHeight } as any}>
        <table className={tableClasses} style={{ minWidth: `${tableWidth}px`, width: 'auto' }}>
          {headerTemplate && <caption>{headerTemplate()}</caption>}
          <PrimeTableHeader
            sortedColumnsWithIndices={getSortedColumnsWithIndices()}
            tableWidth={tableWidth}
            rowExpansionTemplate={rowExpansionTemplate !== undefined}
            selectionMode={selectionMode}
            isAllSelected={isAllSelected}
            isSomeSelected={isSomeSelected}
            handleSelectAll={handleSelectAll}
            frozenColumns={frozenColumns}
            frozenPositions={frozenPositions}
            getColumnWidth={getColumnWidth}
            handleSort={handleSort}
            renderSortIcon={renderSortIcon}
            handleColumnPin={handleColumnPin}
            handleResizeStart={handleResizeStart}
          />
          <PrimeTableBody
            paginatedData={paginatedData}
            columns={columns}
            rowExpansionTemplate={rowExpansionTemplate}
            selectionMode={selectionMode}
            emptyMessage={emptyMessage}
            first={first}
            dataKey={dataKey}
            selection={selection}
            compareSelectionBy={compareSelectionBy}
            isRowExpanded={isRowExpanded}
            onRowClick={onRowClick}
            handleRowClick={handleRowClick}
            handleRowExpand={handleRowExpand}
            getSortedColumnsWithIndices={getSortedColumnsWithIndices}
            frozenColumns={frozenColumns}
            frozenPositions={frozenPositions}
            getColumnWidth={getColumnWidth}
          />
          {footerTemplate && (
            <tfoot>
              <tr>
                <td
                  colSpan={
                    columns.filter((c) => c.visible !== false).length +
                    (rowExpansionTemplate !== undefined ? 1 : 0) +
                    (selectionMode === 'checkbox' ? 1 : 0)
                  }
                >
                  {footerTemplate()}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <PrimeTablePaginator
        paginator={paginator}
        first={first}
        currentRows={currentRows}
        recordCount={recordCount}
        currentPage={currentPage}
        pageCount={pageCount}
        handlePageChange={handlePageChange}
        rowsPerPageOptions={rowsPerPageOptions}
      />
    </div>
  );
});

PrimeTable.displayName = 'PrimeTable';

export default PrimeTable;
